import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Lands from the emailed link. Public on purpose: mail clients open links in
 * whichever browser they please, which is very often not the one the account
 * was created in, so requiring a session here would strand people.
 */
export default function VerifyEmail() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { user, refresh } = useAuth();
  const token = params.get("token") || "";
  const [state, setState] = useState("working"); // working | done | error
  const [error, setError] = useState("");
  // React 18 StrictMode mounts effects twice in development; without this the
  // token would be spent by the first run and the second would report "used".
  const sent = useRef(false);

  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    if (!token) {
      setState("error");
      setError(t("auth.verifyEmail.invalid"));
      return;
    }
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setState("done");
        // Unconditional. It used to be `if (user)`, and that is why the
        // "Confirm your email" banner survived confirming: this page is
        // normally opened cold from a mail client, so at the instant this
        // effect runs AuthContext is still resolving and `user` is null. The
        // refresh never fired, the session arrived a moment later carrying a
        // stale `email_verified: false`, and the banner stayed up until a
        // manual reload.
        //
        // Calling it while signed out is harmless: `refresh` is `checkAuth`,
        // which GETs /auth/me and treats a 401 as "nobody is here".
        refresh();
      } catch (err) {
        setState("error");
        setError(errorMessage(err, t("auth.verifyEmail.invalid")));
      }
    })();
  }, [token, t, refresh]);

  return (
    <div className="min-h-screen bg-linen flex items-center justify-center p-4" data-testid="verify-email-page">
      <div className="w-full max-w-md">
        <div className="card-base p-8 text-center">
          {state === "working" && (
            <>
              <Loader2 size={48} className="mx-auto text-smoke mb-3 animate-spin" />
              <h1 className="font-display text-2xl font-bold text-worker">
                {t("auth.verifyEmail.working")}
              </h1>
            </>
          )}
          {state === "done" && (
            <div data-testid="verify-email-success">
              <CheckCircle2 size={48} className="mx-auto text-green-600 mb-3" />
              <h1 className="font-display text-2xl font-bold text-worker">
                {t("auth.verifyEmail.doneTitle")}
              </h1>
              <p className="mt-2 text-smoke text-sm">{t("auth.verifyEmail.doneBody")}</p>
              <Link to={user ? "/" : "/login"} className="fx-btn fx-btn--primary mt-6 inline-block">
                {user ? t("auth.verifyEmail.continue") : t("auth.backToLogin")}
              </Link>
            </div>
          )}
          {state === "error" && (
            <div data-testid="verify-email-error">
              <AlertCircle size={48} className="mx-auto text-red-600 mb-3" />
              <h1 className="font-display text-2xl font-bold text-worker">
                {t("auth.verifyEmail.errorTitle")}
              </h1>
              <p className="mt-2 text-smoke text-sm">{error}</p>
              <Link to={user ? "/" : "/login"} className="fx-btn fx-btn--secondary mt-6 inline-block">
                {user ? t("auth.verifyEmail.continue") : t("auth.backToLogin")}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
